import React, { useState } from "react";
import '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBriefcase, faSchool, faChalkboardTeacher, faFlask, faTools } from '@fortawesome/free-solid-svg-icons';
import { VerticalTimeline, VerticalTimelineElement }  from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';
import '../assets/styles/Timeline.scss';

// --- IMPORTS FOR THE POPUP ---
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip'; // Added for visual flair
import Box from '@mui/material/Box'; // Added for layout

// ... [KEEP YOUR careerHistory ARRAY EXACTLY AS IT IS] ...
const careerHistory = [
  {
    title: "Graduate Research Assistant",
    subtitle: "Herrick Laboratories | West Lafayette, IN",
    date: "Aug 2024 - Present",
    icon: faFlask,
    brief:"Constructing NMPC using finite volume dynamic model for a Dual-Evaporator BEV thermal system to optimize refrigerant flow and cooling.",
    details: `
    RESEARCH FOCUS:
    Developing advanced thermal management strategies for heavy-duty Battery Electric Vehicles (BEVs), specifically targeting the trade-off between cabin comfort and battery safety under stochastic loads.

    KEY ACHIEVEMENTS:
    - Engineered a Nonlinear Model Predictive Controller (NMPC) utilizing the SLSQP solver to optimize cooling resource allocation.
    - Achieved a reduction in cabin warm-up time from 100s (benchmark PID) to 20s while maintaining battery temps within safe limits.
    - Formulated a novel graph-based modeling approach using matrix stacking to automate component coupling, enabling the synthesis of thermal architectures in <1 hour.

    TECHNICAL IMPACT:
    - Constructed a finite volume discretized dynamic model in Python with dual-evaporator vapor compression dynamics.
    - Created an automated PID tuning framework that minimizes a weighted cost function of peak overshoot, settling time, and control effort.
    `
  },
  {
    title: "Head Graduate Teaching Assistant",
    subtitle: "Purdue University (ME 375) | West Lafayette, IN",
    date: "Aug 2024 - Present",
    icon: faChalkboardTeacher,
    brief: "Spearheaded the redesign of the core Undergraduate Controls curriculum (ME 375). Integrated industrial hardware (Wind Tunnels, Servo Tables) and real-time NI myRIO controllers to bridge theory with practice for 180+ students.",
    details: `
    LEADERSHIP & OPERATIONS:
    - Manage a team of 7 Graduate Teaching Assistants and coordinate operations with faculty to streamline weekly lab delivery.
    - Ensure 100% equipment readiness and consistent grading standards for 180+ students per semester.

    CURRICULUM MODERNIZATION:
    - Evaluated and integrated optimal hardware configurations, specifically Wind Tunnels for air velocity control and Servo Tables for position control.
    - Modernized course documentation by authoring comprehensive lab manuals using MATLAB Live Scripts (covering System ID, PID tuning, and Lead/Lag design).

    PEDAGOGICAL IMPACT:
    - Instituted in-lab "checkpoints" using guided questioning to actively scaffold student learning rather than simply verifying results.
    - Bridged the gap between control theory and practical hardware implementation using NI myRIO controllers and QUARC real-time software.
        `
  },
  {
    title: "BEV Systems Validation Intern",
    subtitle: "Daimler Truck North America | Detroit, MI",
    date: "Aug 2023 - Nov 2023",
    icon: faBriefcase,
    brief: "Advanced a unified validation platform using custom Python modules to correlate HIL/SIL simulations. Implemented automated batch sorting that reduced CAN MDF data file processing time by over 85%.",
    details: `
      AUTOMATION & EFFICIENCY:
      - Diagnosed a critical bottleneck in field data processing caused by unsorted MDF files; implemented a batch sorting routine reducing processing time from ~15 mins to <2 mins (85% reduction) per file.
      - Engineered an automated Reference Performance Test (RPT) script for High Voltage batteries that manages UDS Security Access and eliminates manual signal manipulation.

      VALIDATION FRAMEWORK (ECU-TEST):
      - Developed custom Python modules (NumPy, Matplotlib) to automate energy consumption analysis across HIL, SIL, and Chassis Dynamometer environments.
      - Leveraged these tools to validate vehicle performance during critical "Grade Pull" scenarios, enabling direct data correlation between models and physical testing.

      DATA VISUALIZATION:
      - Prototyped a Power BI dashboard to allow engineers to drill down from high-level Platform trends to low-level VIN-specific test runs.
    `
  },
  {
    title: "Aftertreatment Performance Intern",
    subtitle: "Daimler Truck North America | Detroit, MI",
    date: "May 2023 - Aug 2023",
    icon: faBriefcase,
    brief: "Developed a patent-pending semi-empirical predictive model for NOx conversion in SCR catalysts, achieving ±10% accuracy. Engineered a Python-based Automated Field Issue Analyzer to detect intermittent control logic failures.",
    details: `
      INNOVATION (PATENT-PENDING):
      - Developed a semi-empirical predictive model for NOx conversion in Copper Zeolite SCR catalysts to characterize performance degradation during sulfur poisoning.
      - Validated the model through extensive engine dynamometer testing, achieving ±10% accuracy compared to physical sensors.

      AUTOMATION & DIAGNOSTICS:
      - Engineered a Python-based "Automated Field Issue Analyzer" that detects intermittent control logic failures in customer vehicle logs.
      - Reduced manual diagnosis time by 2 hours per week and accelerated root cause analysis for field faults.

      TESTING & COMPLIANCE:
      - Executed Diesel Particulate Filter (DPF) soot loading campaigns on engine test cells to support EPA Emissions Certification.
      - Automated the analysis of off-highway drive cycles (from customer trucks) using MATLAB to ensure emissions robustness under non-standard real world transient loads.
    `
  },
  {
    title: "Manufacturing Peer Mentor",
    subtitle: "Bechtel Innovation Design Center | West Lafayette, IN",
    date: "Feb 2022 - May 2024",
    icon: faTools,
    brief: "Mentored over 100 students annually in advanced CAM programming (Fusion 360) and 5-axis CNC machining. Conducted Design for Manufacturing (DFM) reviews to optimize component geometry for fabrication.",
    details: `
      MENTORSHIP & LEADERSHIP:
      - Mentored over 100 students annually for 10 hours/week in advanced manufacturing methodologies.
      - Provided technical consultation on GD&T and CAM programming to support a wide range of academic and research projects.

      TECHNICAL OPERATIONS:
      - Conducted Design for Manufacturing (DFM) reviews, advising on component redesigns to optimize geometry for CNC milling and turning.
      - Provided hands-on instruction for industrial machinery including Haas 3-axis (VF2/VF4) mills, 5-axis (UMC 500) mills, and Haas ST20Y lathes.
      - Enabled students to execute complex fabrication tasks safely by bridging the gap between CAD design and physical machining.
    `
  },
  {
    title: "Test Engineer Co-op",
    subtitle: "Cummins Inc. | Stoughton, WI",
    date: "June 2021 - Dec 2021",
    icon: faBriefcase,
    brief: "Served as Primary Test Engineer for Test Cell 22, managing R&D testing for the Next-Gen DRD launch. Designed an automated test fixture that reduced technician setup time by 87%.",
    details: `
      TEST ENGINEERING & OPERATIONS:
      - Served as the primary Test Engineer for Test Cell 22, managing R&D testing for the Next-Gen Decomposition Reactor (DRD).
      - Orchestrated daily test cell operations, balancing strict equipment calibration schedules with high-priority project timelines.

      AUTOMATION & DESIGN:
      - Designed and fabricated an automated test fixture for a novel laser-based gas analyzer using Creo and a Controllino (Industrial Arduino PLC).
      - Reduced technician setup time by 87% and enabled the feasible deployment of this measurement technology in active test cells.

      CONTROLS & ANALYSIS:
      - Optimized test cell HVAC PID control parameters to maintain stable ambient boundary conditions during volatile edge-season weather.
      - Led Root Cause Analysis (RCA) investigations using Fault Tree Analysis to resolve critical downtime events, including dynamometer trip faults and driveline resonance.
    `
  },
];

function Timeline() {
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  const handleClickOpen = (item: any) => {
    setSelectedItem(item);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedItem(null);
  };

// --- HELPER 1: BOLD PERCENTAGES (Corrected) ---
  const boldPercent = (text: string) => {
    if (!text) return null;
    const parts = text.split(/(\d+%)/g);
    // REMOVED: style={{color: '#fff'}} 
    // NOW: It just uses <strong> which makes it bold but keeps the parent text color
    return parts.map((part, idx) => (/\d+%/.test(part) ? <strong key={idx}>{part}</strong> : part));
  };

  // --- HELPER 2: SMART PARSER FOR DETAILS ---
  // This function takes your string, detects Headers (ending in :) and Bullets (starting with -)
  const renderDetails = (details: string) => {
    if (!details) return null;

    const lines = details.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    const content: JSX.Element[] = [];
    let currentList: JSX.Element[] = [];

    lines.forEach((line, index) => {
      // CASE 1: It's a Header (Ends with :)
      if (line.endsWith(':')) {
        // If we had a list building up, push it first
        if (currentList.length > 0) {
          content.push(<ul key={`list-${index}`} style={{ margin: '0 0 15px 20px', paddingLeft: '0' }}>{currentList}</ul>);
          currentList = [];
        }
        // Push the Header
        content.push(
          <Typography 
            key={`header-${index}`} 
            variant="subtitle1" 
            style={{ 
              color: '#a685e2', // Light purple accent
              fontWeight: 'bold', 
              marginTop: '15px', 
              marginBottom: '5px',
              borderBottom: '1px solid rgba(80, 0, 202, 0.3)',
              paddingBottom: '2px',
              display: 'inline-block'
            }}
          >
            {line}
          </Typography>
        );
      } 
      // CASE 2: It's a Bullet Point (Starts with -)
      else if (line.startsWith('-')) {
        const text = line.substring(1).trim(); // Remove the dash
        currentList.push(
          <li key={`li-${index}`} style={{ marginBottom: '8px', color: '#ddd', lineHeight: '1.6' }}>
            {boldPercent(text)}
          </li>
        );
      } 
      // CASE 3: Standard text
      else {
         // If we had a list, push it
         if (currentList.length > 0) {
          content.push(<ul key={`list-${index}`} style={{ margin: '0 0 15px 20px', paddingLeft: '0' }}>{currentList}</ul>);
          currentList = [];
        }
        content.push(
          <Typography key={`p-${index}`} paragraph style={{ color: '#ccc', marginBottom: '10px' }}>
            {boldPercent(line)}
          </Typography>
        );
      }
    });

    // Flush any remaining list items
    if (currentList.length > 0) {
      content.push(<ul key="list-end" style={{ margin: '0 0 15px 20px', paddingLeft: '0' }}>{currentList}</ul>);
    }

    return content;
  };

  return (
    <div id="history">
      <div className="items-container">
        <h1>Career History</h1>
        <VerticalTimeline>
          {careerHistory.map((item, index) => (
            <VerticalTimelineElement
              key={index}
              className="vertical-timeline-element--work"
              contentStyle={{ background: 'white', color: 'rgb(39, 40, 34)' }}
              contentArrowStyle={{ borderRight: '7px solid  white' }}
              date={item.date}
              iconStyle={{ background: '#5000ca', color: 'rgb(255, 255, 255)' }}
              icon={<FontAwesomeIcon icon={item.icon} />}
            >
              <h3 
                className="vertical-timeline-element-title" 
                onClick={() => handleClickOpen(item)}
                style={{cursor: 'pointer', textDecoration: 'underline', textDecorationColor: '#5000ca'}}
              >
                {item.title}
              </h3>
              
              <h4 className="vertical-timeline-element-subtitle">{item.subtitle}</h4>
              <p>{boldPercent(item.brief)}</p>
            </VerticalTimelineElement>
          ))}
        </VerticalTimeline>

        {/* --- ELEGANT POPUP WINDOW --- */}
        <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="md" 
            fullWidth={true}
            // Add a subtle border glow
            PaperProps={{
                style: { 
                  backgroundColor: '#1a1a1a', 
                  color: 'white', 
                  borderRadius: '12px',
                  border: '1px solid #333',
                  boxShadow: '0 0 20px rgba(80, 0, 202, 0.2)'
                }
            }}
        >
            {selectedItem && (
                <>
                    {/* Header Section */}
                    <DialogTitle style={{
                        backgroundColor: '#131313', 
                        borderBottom: '1px solid #333', 
                        padding: '20px 24px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '15px'
                    }}>
                        {/* Render Icon in Title */}
                        <Box style={{
                            backgroundColor: '#2d2d2d', 
                            padding: '10px', 
                            borderRadius: '8px', 
                            color: '#5000ca',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                          <FontAwesomeIcon icon={selectedItem.icon} size="lg" />
                        </Box>

                        <Box>
                          <Typography variant="h5" style={{fontWeight: 'bold', color: '#fff'}}>
                              {selectedItem.title}
                          </Typography>
                          <Typography variant="subtitle2" style={{color: '#888', marginTop: '2px'}}>
                             {selectedItem.subtitle}
                          </Typography>
                        </Box>
                    </DialogTitle>

                    {/* Content Section */}
                    <DialogContent style={{padding: '24px', backgroundColor: '#1a1a1a'}}>
                        
                        {/* Date Chip */}
                        <Box style={{marginBottom: '20px', marginTop: '10px'}}>
                           <Chip 
                              label={selectedItem.date} 
                              size="small" 
                              style={{
                                backgroundColor: '#5000ca', 
                                color: 'white', 
                                fontWeight: 'bold'
                              }} 
                            />
                        </Box>

                        {/* Render the Parsed Details */}
                        {renderDetails(selectedItem.details)}

                    </DialogContent>

                    {/* Actions Section */}
                    <DialogActions style={{backgroundColor: '#131313', borderTop: '1px solid #333', padding: '15px 24px'}}>
                        <Button 
                          onClick={handleClose} 
                          variant="text" 
                          style={{color: '#ccc', fontWeight: 'bold'}}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>

      </div>
    </div>
  );
}

export default Timeline;